import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, Save, GripVertical, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = ["pendiente", "en_curso", "completado"];
const TIPO_OPTS = ["Cognitivo", "Emocional", "Pedagógico"];

export default function EditarPlan() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const seg = pathname.split("/")[1];
  const base = `/${["admin", "docente", "franquiciado"].includes(seg) ? seg : "docente"}/estudiante/${id}`; // v5.6.1 · prefijo según el rol
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [ruta, setRuta] = useState([]);
  const [intervencion, setIntervencion] = useState([]);
  const [mapa, setMapa] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: p }, { data: r }] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/students/${id}/plan`),
        api.get(`/students/${id}/recommendations`),
      ]);
      setStudent(s);
      setRuta(p.ruta || []);
      setIntervencion(p.intervencion || []);
      setMapa(p.mapa_clases || []);
      setRecommendations(r.recommendations || []);
    })();
  }, [id]);

  if (!student) return <p className="text-[#8A9499]">Cargando…</p>;

  const addEtapa = () =>
    setRuta([...ruta, {
      id: `r${Date.now()}`, title: "Nueva etapa", status: "pendiente", weeks: 2, topics: ["Tema 1"],
    }]);
  const removeEtapa = (i) => setRuta(ruta.filter((_, x) => x !== i));
  const updateEtapa = (i, patch) => setRuta(ruta.map((r, x) => x === i ? { ...r, ...patch } : r));

  const addInterv = () =>
    setIntervencion([...intervencion, { id: `i${Date.now()}`, tipo: "Cognitivo", accion: "" }]);
  const removeInterv = (i) => setIntervencion(intervencion.filter((_, x) => x !== i));
  const updateInterv = (i, patch) => setIntervencion(intervencion.map((it, x) => x === i ? { ...it, ...patch } : it));

  const addMapa = () =>
    setMapa([...mapa, { week: mapa.length + 1, title: "Sesión", completed: false }]);
  const removeMapa = (i) => setMapa(mapa.filter((_, x) => x !== i).map((m, idx) => ({ ...m, week: idx + 1 })));
  const updateMapa = (i, patch) => setMapa(mapa.map((m, x) => x === i ? { ...m, ...patch } : m));

  const importRec = (rec) => {
    setIntervencion([...intervencion, {
      id: `i${Date.now()}`,
      tipo: rec.type === "academico" ? "Pedagógico" : rec.type === "cognitivo" ? "Cognitivo" : "Emocional",
      accion: rec.action,
    }]);
    toast.success("Recomendación añadida al plan de intervención");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/students/${id}/plan`, { ruta, intervencion, mapa_clases: mapa });
      toast.success("Plan guardado correctamente");
      navigate(base);
    } catch {
      toast.error("Error al guardar el plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="editar-plan-page">
      <Link to={base} className="inline-flex items-center gap-1.5 text-sm text-[#5C666B] hover:text-[#C87A5B]">
        <ArrowLeft size={15} /> Volver al perfil
      </Link>

      <div className="premium-card p-6 flex flex-wrap items-start gap-4 justify-between">
        <div className="flex items-center gap-4">
          <img src={student.avatar} alt="" className="w-14 h-14 rounded-full bg-[#F3EFEA] border border-[#E8E2D9]" />
          <div>
            <span className="tag-premium">Constructor de ruta</span>
            <h1 className="font-display text-2xl font-medium text-[#2C3539] mt-1">Plan personalizado · {student.name}</h1>
            <p className="text-sm text-[#5C666B]">Diseña la ruta de aprendizaje, plan de intervención y mapa de clases.</p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-plan"
          className="inline-flex items-center gap-2 bg-[#C87A5B] text-white px-5 py-2.5 rounded-md font-medium hover:bg-[#B36B4F] disabled:opacity-50"
        ><Save size={16} /> {saving ? "Guardando…" : "Guardar plan"}</button>
      </div>

      {recommendations.length > 0 && (
        <div className="premium-card p-6" data-testid="ai-recommendations-panel">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#B8985C]" />
            <h3 className="font-display text-lg font-medium text-[#2C3539]">Recomendaciones</h3>
          </div>
          <p className="text-sm text-[#5C666B] mb-4">Sugerencias automáticas basadas en el perfil integrado del estudiante. Haz clic para añadir al plan.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {recommendations.map((r, i) => (
              <button
                key={i}
                data-testid={`add-rec-${i}`}
                onClick={() => importRec(r)}
                className="text-left p-4 bg-[#FAF7F2] border border-[#E8E2D9] rounded-md hover:border-[#B8985C] hover:bg-[#FBF6E8] transition-colors group"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`tag-pill ${
                    r.priority === "alta" ? "bg-[#B85C5C]/15 text-[#8a4646]" :
                    r.priority === "media" ? "bg-[#D4A373]/20 text-[#7a5d3e]" :
                    "bg-[#7A9E7E]/15 text-[#5b7e60]"
                  }`}>{r.priority}</span>
                  <Plus size={14} className="text-[#8A9499] group-hover:text-[#B8985C]" />
                </div>
                <p className="text-sm font-medium text-[#2C3539]">{r.title}</p>
                <p className="text-xs text-[#5C666B] mt-1">{r.action}</p>
                <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#8A9499] mt-2">{r.duration}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ruta */}
      <section className="premium-card p-6" data-testid="edit-ruta">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-medium text-[#2C3539]">Ruta de aprendizaje</h3>
          <button onClick={addEtapa} data-testid="add-etapa" className="text-sm font-medium text-[#C87A5B] hover:text-[#B36B4F] inline-flex items-center gap-1.5">
            <Plus size={14} /> Añadir etapa
          </button>
        </div>
        <div className="space-y-3">
          {ruta.map((r, i) => (
            <div key={r.id} className="border border-[#E8E2D9] rounded-md p-4 bg-[#FAF7F2]" data-testid={`etapa-${i}`}>
              <div className="flex items-start gap-3">
                <GripVertical size={16} className="text-[#8A9499] mt-2 shrink-0" />
                <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <input
                    value={r.title}
                    onChange={(e) => updateEtapa(i, { title: e.target.value })}
                    className="md:col-span-5 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm"
                    placeholder="Título de la etapa"
                  />
                  <input
                    value={r.topics.join(", ")}
                    onChange={(e) => updateEtapa(i, { topics: e.target.value.split(",").map((s) => s.trim()) })}
                    className="md:col-span-4 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm"
                    placeholder="Temas separados por coma"
                  />
                  <input
                    type="number"
                    min={1}
                    value={r.weeks}
                    onChange={(e) => updateEtapa(i, { weeks: parseInt(e.target.value) || 1 })}
                    className="md:col-span-1 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm"
                  />
                  <select
                    value={r.status}
                    onChange={(e) => updateEtapa(i, { status: e.target.value })}
                    className="md:col-span-2 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm capitalize"
                  >
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
                <button onClick={() => removeEtapa(i)} className="text-[#B85C5C] hover:bg-[#B85C5C]/10 p-1.5 rounded">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {ruta.length === 0 && <p className="text-sm text-[#8A9499]">Aún no hay etapas. Añade la primera.</p>}
        </div>
      </section>

      {/* Intervención */}
      <section className="premium-card p-6" data-testid="edit-intervencion">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-medium text-[#2C3539]">Plan de intervención</h3>
          <button onClick={addInterv} data-testid="add-intervencion" className="text-sm font-medium text-[#C87A5B] hover:text-[#B36B4F] inline-flex items-center gap-1.5">
            <Plus size={14} /> Añadir acción
          </button>
        </div>
        <div className="space-y-3">
          {intervencion.map((it, i) => (
            <div key={it.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`interv-${i}`}>
              <select
                value={it.tipo}
                onChange={(e) => updateInterv(i, { tipo: e.target.value })}
                className="md:col-span-2 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm"
              >
                {TIPO_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <textarea
                value={it.accion}
                onChange={(e) => updateInterv(i, { accion: e.target.value })}
                className="md:col-span-9 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm"
                placeholder="Acción concreta…"
                rows={2}
              />
              <button onClick={() => removeInterv(i)} className="md:col-span-1 text-[#B85C5C] hover:bg-[#B85C5C]/10 p-1.5 rounded justify-self-end">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {intervencion.length === 0 && <p className="text-sm text-[#8A9499]">Sin acciones. Las recomendaciones se pueden importar arriba.</p>}
        </div>
      </section>

      {/* Mapa */}
      <section className="premium-card p-6" data-testid="edit-mapa">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-medium text-[#2C3539]">Mapa de clases</h3>
          <button onClick={addMapa} data-testid="add-mapa" className="text-sm font-medium text-[#C87A5B] hover:text-[#B36B4F] inline-flex items-center gap-1.5">
            <Plus size={14} /> Añadir semana
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {mapa.map((m, i) => (
            <div key={i} className="flex items-center gap-2 bg-[#FAF7F2] border border-[#E8E2D9] rounded-md p-2" data-testid={`mapa-${i}`}>
              <span className="font-display text-sm font-medium text-[#C87A5B] w-8 text-center">S{m.week}</span>
              <input
                value={m.title}
                onChange={(e) => updateMapa(i, { title: e.target.value })}
                className="flex-1 bg-white border border-[#E8E2D9] rounded-md px-3 py-2 text-sm"
                placeholder="Tema de la sesión"
              />
              <label className="text-xs text-[#5C666B] inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={!!m.completed}
                  onChange={(e) => updateMapa(i, { completed: e.target.checked })}
                /> Hecha
              </label>
              <button onClick={() => removeMapa(i)} className="text-[#B85C5C] hover:bg-[#B85C5C]/10 p-1.5 rounded">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
